import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';
import { canFallBackOffline } from '@/lib/api/offlineFallback';
import { useSession } from '@/lib/auth/session';
import { TOUR_VERSION } from '@/lib/tour/steps';

/**
 * "이 사람이 안내를 봤는가" — 서버에 남는 기록.
 *
 * ## 왜 서버인가
 *
 * 사무실 PC·현장 노트북·휴대폰을 오가는 사용자가 많다. 브라우저에만 남기면 기기를 바꿀 때마다
 * 안내가 다시 뜬다 — 이미 아는 사람에게 세 번째로 뜨는 안내는 방해일 뿐이다. 홈 위젯 배치를
 * 서버에 두는 것과 같은 이유다({@link useHomeWidgets}).
 *
 * ## 왜 불리언이 아니라 판번호인가
 *
 * 대본은 기능이 늘면 함께 바뀐다. "봤다/안 봤다" 한 칸이면 대본을 고쳐도 기존 사용자는 영영 새 안내를
 * 보지 못한다. 마지막으로 본 판번호를 남겨 두고, {@link TOUR_VERSION} 이 그보다 크면 다시 띄운다.
 *
 * 저장은 낙관적으로 한다 — 안내를 닫는 즉시 화면에서는 "봤음" 이 되고, 서버 왕복은 뒤에서 끝난다.
 * 저장이 실패하면 다음 접속에서 한 번 더 뜰 뿐이라 되돌릴 것이 없다.
 */

const TOURS_PATH = '/v1/tours';
const STORAGE_PREFIX = 'bomi.tour.seen';

type TourProgressRow = {
  tourKey: string;
  seenVersion: number;
  completed: boolean;
};

export type TourProgress = {
  /** 기록을 아직 받아오지 못했으면 false — 그동안에는 자동 실행을 미룬다. */
  loaded: boolean;
  /** 이 계정에게 안내를 자동으로 띄워야 하는가. */
  shouldAutoStart: boolean;
  /** 안내를 닫을 때 부른다. `completed` 는 끝까지 봤는지 여부다(건너뛰기는 false). */
  markSeen: (completed: boolean) => void;
};

/**
 * @param version 이 안내의 판번호. 기본은 전체 안내의 {@link TOUR_VERSION} 이고,
 *   <b>자기 판번호로 움직이는 안내</b>는 직접 넘긴다({@code WHATS_NEW_VERSION}) — 전체 안내를
 *   고쳤다고 「바뀐 것」 안내까지 다시 뜨면, 그때는 이미 바뀐 것이 아닌 말을 다시 하게 된다.
 */
export function useTourProgress(tourKey: string, version: number = TOUR_VERSION): TourProgress {
  const { data: session } = useSession();
  const userId = session?.user?.id ?? null;
  // 비밀번호를 바꿔야 하는 계정은 곧 다른 화면으로 밀려난다. 그 위에 안내를 얹지 않는다.
  const eligible = !!userId && session?.authenticated === true && !session.user?.mustChangePassword;

  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ['tour-progress', userId] as const, [userId]);

  const { data, isSuccess } = useQuery({
    queryKey,
    queryFn: () => fetchProgress(userId as string),
    enabled: eligible,
    // 안내 기록은 이 세션 동안 바뀌지 않는다. 화면을 옮길 때마다 다시 묻지 않는다.
    staleTime: Infinity,
  });

  const seenVersion = data?.find((row) => row.tourKey === tourKey)?.seenVersion ?? 0;

  const markSeen = useCallback(
    (completed: boolean) => {
      if (!userId) return;

      const next: TourProgressRow[] = [
        ...(data ?? []).filter((row) => row.tourKey !== tourKey),
        { tourKey, seenVersion: version, completed },
      ];
      queryClient.setQueryData(queryKey, next);

      persistProgress(userId, tourKey, version, completed).catch((error) => {
        console.error('[tour] 안내 기록 저장 실패', error);
      });
    },
    [data, queryClient, queryKey, tourKey, userId, version],
  );

  return {
    loaded: isSuccess,
    shouldAutoStart: eligible && isSuccess && seenVersion < version,
    markSeen,
  };
}

// ── 서버 왕래 (오프라인이면 localStorage 로 물러선다 — 개발용) ────────────────────

async function fetchProgress(userId: string): Promise<TourProgressRow[]> {
  try {
    const rows = await apiFetch<TourProgressRow[] | null>(TOURS_PATH);
    return Array.isArray(rows) ? rows.filter(isProgressRow) : [];
  } catch (error) {
    if (canFallBackOffline(error)) return readLocal(userId);
    throw error;
  }
}

async function persistProgress(
  userId: string,
  tourKey: string,
  version: number,
  completed: boolean,
): Promise<void> {
  try {
    await apiFetch<unknown>(`${TOURS_PATH}/${encodeURIComponent(tourKey)}`, {
      method: 'PUT',
      body: { version, completed },
    });
  } catch (error) {
    if (canFallBackOffline(error)) {
      writeLocal(userId, tourKey, version, completed);
      return;
    }
    throw error;
  }
}

// 저장 키에 사용자 id 를 넣는다 — 같은 PC 를 여러 사람이 쓰는 현장 사무실이 있다.
function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}.${userId}`;
}

function readLocal(userId: string): TourProgressRow[] {
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return [];

    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isProgressRow) : [];
  } catch {
    // 사생활 보호 모드라 접근이 막혔거나 JSON 이 깨졌을 뿐이다. 안내가 한 번 더 뜨면 그만이다.
    return [];
  }
}

function writeLocal(userId: string, tourKey: string, version: number, completed: boolean): void {
  const next: TourProgressRow[] = [
    ...readLocal(userId).filter((row) => row.tourKey !== tourKey),
    { tourKey, seenVersion: version, completed },
  ];

  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(next));
  } catch {
    // 저장을 못 해도 이번 세션 동안은 다시 뜨지 않는다(쿼리 캐시가 들고 있다).
  }
}

function isProgressRow(value: unknown): value is TourProgressRow {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;

  return (
    typeof candidate.tourKey === 'string' &&
    typeof candidate.seenVersion === 'number' &&
    typeof candidate.completed === 'boolean'
  );
}
